import React, { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useGlobalState } from '../../../Providers';
import { CONTROL_PANEL_WIDGET_BUTTONS, DEFAULT_WIDGET, LIVE_SETTING_ID, QUICK_ENROL_SETTING_ID, WIDGET_TYPES } from "../../../constants/widget-constants";
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { useLocale } from '../../../contexts/localeContext';
import commandIcon from '../../../images/Commands - Icon.svg';
import enrolIcon from '../../../images/Enrol - Icon.svg';
import liveTransactionsIcon from '../../../images/Live Transactions - Icon White.svg';
import ShortcutIcon from '../../../images/Shortcut - Icon.svg';
import { ListItemOption } from '../../../models/ListItemOption';
import { Widget } from '../../../models/Widget';
import { getErrorMessage, showError } from '../../../services/utilities/ServiceError';
import { doesWidgetExist, storeWidget } from '../../../services/widgetService';
import { IAppState } from '../../../store/types';
import { useDialog } from '../../dialog/DialogContext';
import List from '../../list/list';
import '.././control-panel/control-panel.css';
import PanelItem from './PanelItem';
import ControlPanelWizard from "./control-panel-wizard";

const ControlPanel: FC = () => {
    const [expandPanel, setExpandPanel] = useState(false);
    const globalContext = useGlobalState(); 
    const [selectedItemIndex, setSelectedItemIndex] = useState<number | undefined>(0);
    const { translate } = useLocale();
    const [open, setOpen] = useState(false);
    const user = useSelector((state: IAppState) => (state.userInfo));
    const dialog = useDialog();

    const getControlPanelItems = () => {
        let controlPanelItems: ListItemOption[] = [
            {
                value: <PanelItem itemValue={translate.byKey(TranslationKeys.shortcut)} expandPanel={expandPanel} selected={selectedItemIndex === 2}>
                    <img src={ShortcutIcon} className="h-5 w-5" alt="" id='shortcutPanelItemImage' />
                </PanelItem>,
                isSelected: false,
                key: 2,
            },
        ]

        if (user.permissions.includes("add.hardware.element")) {
            controlPanelItems.push(
                {
                    value: <PanelItem itemValue={translate.byKey(TranslationKeys.Commands)} expandPanel={expandPanel} selected={selectedItemIndex === 0}>
                        <img src={commandIcon} className="h-5 w-5" alt="" id="commandPanelItemImage" />
                    </PanelItem>,
                    isSelected: false,
                    key: 0,
                }
            )
        }

        if (user.permissions.includes("show.transactions.element")) {
            controlPanelItems.push(
                {
                    value: <PanelItem itemValue={translate.byKey(TranslationKeys.liveTransactions)} expandPanel={expandPanel} selected={selectedItemIndex === 1}>
                        <img src={liveTransactionsIcon} className="h-5 w-5" alt="" id='liveTransactionsPanelItemImage' />
                    </PanelItem>,
                    isSelected: false,
                    key: 1,
                }
            )
        }
        
        if (user.permissions.includes("add.enrolment.element")) {
            controlPanelItems.push(
                {
                    value: <PanelItem itemValue={translate.byKey(TranslationKeys.enrol)} expandPanel={expandPanel} selected={selectedItemIndex === 3}>
                        <img src={enrolIcon} className="h-5 w-5" alt="enrol" />
                    </PanelItem>,
                    isSelected: false,
                    key: 3,
                }
            )
        }

        // Sort the array by the 'key' property
        controlPanelItems.sort((a, b) => a.key - b.key);

        return controlPanelItems;
    }

    const onExpandPanel = () => {
        setExpandPanel(!expandPanel);
    }
    
    const onLayoutItemClicked = async (index: number) => {
        try {
            const listItemKey = getControlPanelItems()[index].key;
            setSelectedItemIndex(listItemKey);

            if (listItemKey === 0) {
                setOpen(true);
                globalContext?.setControlPanelButton(CONTROL_PANEL_WIDGET_BUTTONS.COMMAND);
            }
            else if (listItemKey === 1) {
                const widgetExist = await doesWidgetExist(user.id, WIDGET_TYPES.LIVE_TRANSACTIONS_WIDGET);
                if (widgetExist) {
                    dialog.pushError({ title: translate.byKey(TranslationKeys.liveTransactions), message: translate.byKey(TranslationKeys.widgetAlreadyExistsCannotCreateDuplicateWidget) })
                    return;
                }

                saveWidget(WIDGET_TYPES.LIVE_TRANSACTIONS_WIDGET, LIVE_SETTING_ID.toString());
                globalContext?.setControlPanelButton(CONTROL_PANEL_WIDGET_BUTTONS.LIVE_TRANSACTIONS);
            }
            else if (listItemKey === 2) {
                setOpen(true);
                globalContext?.setControlPanelButton(CONTROL_PANEL_WIDGET_BUTTONS.SHORTCUT);
            }
            else if (listItemKey === 3) {
                const widgetExist = await doesWidgetExist(user.id, WIDGET_TYPES.QUICK_ENROLL_WIDGET);
                if (widgetExist) {
                    dialog.pushError({ title: translate.byKey(TranslationKeys.quickEnrol), message: translate.byKey(TranslationKeys.widgetAlreadyExistsCannotCreateDuplicateWidget) })
                    return;
                }

                saveWidget(WIDGET_TYPES.QUICK_ENROLL_WIDGET, QUICK_ENROL_SETTING_ID.toString());
                globalContext?.setControlPanelButton(CONTROL_PANEL_WIDGET_BUTTONS.QUICK_ENROL);
            }
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    const saveWidget = (type: string, settingIdToCopy: string) => {
        const initialItem: Widget = {
            x: DEFAULT_WIDGET.X_POSITION,
            y: DEFAULT_WIDGET.Y_POSITION,
            width: 0,
            height: 0,
            type,
            settingIdToCopy,
            userLoginId: user.id,
            titleBackgroundColor: DEFAULT_WIDGET.TITLE_BKGCOLOR,
            titleForegroundColor: DEFAULT_WIDGET.TITLE_FGCOLOR,
            id: 0,
            relationships: [],
        };

        storeWidget(initialItem).then(() => {
            globalContext?.setRefreshDashboard(true);
            toast.success(translate.byKey(TranslationKeys.success));
        }).catch((error) => {
            dialog.pushError({
                message: getErrorMessage(error),
                title: translate.byKey(TranslationKeys.failedToCreateWidget)
            });
        })
    }
    
    return (
        <>
            <div className="relative" id='controlPanel'>
                <div className={`absolute overflow-hidden top-0 right-0 w-auto rounded-l-lg menu-item transition-all duration-300 ${expandPanel ? "w-[150px]" : "w-[3.4rem]"}`}
                    style={{ width: expandPanel ? "160px" : "3.4rem", backgroundColor: "#373737" }}
                >
                    <div className='text-right p-2' id={expandPanel ? "compressControlPanel" : "expandControlPanel"}>
                        <span className='cursor-pointer' onClick={onExpandPanel}>{expandPanel ? '<<' : '>>'}</span>
                    </div>
                    <List
                        items={getControlPanelItems()}
                        onSelect={onLayoutItemClicked}
                        isStriped={false}
                        listItemStyle={'bg-transparent'}
                    />
                </div>
            </div>
            <ControlPanelWizard
                isOpen={open}
                onClose={() => setOpen(false)}
                selectedIndex={selectedItemIndex}
            />
        </>
    );
};

export default ControlPanel;
